import { useState, useEffect, useCallback } from 'react';
import {
  getActivityStats,
  getWorkflowSummary,
  getWorkflowTypeStats,
  type ActivityStatsData,
  type WorkflowSummaryData,
  type WorkflowTypeStatsData
} from '../services/analyticsService';
import { FusionNowNotSubscribedError } from '../services/fusionNowAuth';

export function useAnalytics() {
  const [activityStats, setActivityStats] = useState<ActivityStatsData | null>(null);
  const [workflowSummary, setWorkflowSummary] = useState<WorkflowSummaryData | null>(null);
  const [workflowTypeStats, setWorkflowTypeStats] = useState<WorkflowTypeStatsData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notSubscribed, setNotSubscribed] = useState(false);

  const fetchAll = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setNotSubscribed(false);
    try {
      const [activity, summary, typeStats] = await Promise.all([
        getActivityStats(),
        getWorkflowSummary(),
        getWorkflowTypeStats()
      ]);
      setActivityStats(activity);
      setWorkflowSummary(summary);
      setWorkflowTypeStats(typeStats);
    } catch (err) {
      if (err instanceof FusionNowNotSubscribedError) setNotSubscribed(true);
      const msg =
        err instanceof FusionNowNotSubscribedError
          ? 'Not subscribed to MAC'
          : err instanceof Error
            ? err.message
            : 'Failed to load analytics';
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  return {
    activityStats,
    workflowSummary,
    workflowTypeStats,
    isLoading,
    error,
    notSubscribed,
    refetch: fetchAll
  };
}
